import { clsx } from 'clsx';

interface ScoreGaugeProps {
  score: number;
  label: string;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
}

const sizeClasses = {
  sm: { container: 'w-20 h-20', text: 'text-lg', label: 'text-[10px]' },
  md: { container: 'w-28 h-28', text: 'text-2xl', label: 'text-xs' },
  lg: { container: 'w-36 h-36', text: 'text-3xl', label: 'text-sm' },
};

export function ScoreGauge({ score, label, size = 'md', showLabel = true }: ScoreGaugeProps) {
  // Score is 0-100 scale where 50 is neutral
  const normalizedScore = Math.max(0, Math.min(100, score));
  const percentage = normalizedScore; // Already 0-100

  const getColor = () => {
    if (score >= 75) return { stroke: '#10b981', bg: 'rgba(16, 185, 129, 0.1)' }; // Very bullish
    if (score >= 60) return { stroke: '#22c55e', bg: 'rgba(34, 197, 94, 0.1)' };  // Bullish
    if (score >= 40) return { stroke: '#eab308', bg: 'rgba(234, 179, 8, 0.1)' };  // Neutral
    if (score >= 25) return { stroke: '#f97316', bg: 'rgba(249, 115, 22, 0.1)' }; // Bearish
    return { stroke: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)' };                    // Very bearish
  };

  const { stroke, bg } = getColor();
  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <div className="flex flex-col items-center gap-2">
      <div className={clsx('relative', sizeClasses[size].container)}>
        <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
          {/* Background circle */}
          <circle
            cx="50"
            cy="50"
            r="45"
            fill="none"
            stroke="var(--color-border)"
            strokeWidth="8"
          />
          {/* Progress circle */}
          <circle
            cx="50"
            cy="50"
            r="45"
            fill="none"
            stroke={stroke}
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            className="transition-all duration-700 ease-out"
          />
        </svg>
        {/* Center content */}
        <div
          className="absolute inset-0 flex flex-col items-center justify-center rounded-full"
          style={{ backgroundColor: bg }}
        >
          <span
            className={clsx(
              'font-bold font-mono',
              sizeClasses[size].text
            )}
            style={{ color: stroke }}
          >
            {Math.round(score)}
          </span>
        </div>
      </div>
      {showLabel && (
        <span
          className={clsx(
            'font-medium text-[var(--color-text-muted)]',
            sizeClasses[size].label
          )}
        >
          {label}
        </span>
      )}
    </div>
  );
}

interface LinearGaugeProps {
  score: number;
  label: string;
  min?: number;
  max?: number;
}

export function LinearGauge({ score, label, min = 0, max = 100 }: LinearGaugeProps) {
  const normalizedScore = Math.max(min, Math.min(max, score));
  const percentage = ((normalizedScore - min) / (max - min)) * 100;

  const getColor = () => {
    const normalized = (score - min) / (max - min);
    if (normalized >= 0.7) return 'bg-emerald-500';
    if (normalized >= 0.5) return 'bg-green-500';
    if (normalized >= 0.3) return 'bg-yellow-500';
    if (normalized >= 0.15) return 'bg-orange-500';
    return 'bg-red-500';
  };

  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <span className="text-sm font-medium text-[var(--color-text-secondary)]">
          {label}
        </span>
        <span className="text-sm font-mono font-bold text-[var(--color-text-primary)]">
          {Math.round(score)}
        </span>
      </div>
      <div className="h-2 bg-[var(--color-bg-tertiary)] rounded-full overflow-hidden">
        <div
          className={clsx('h-full rounded-full transition-all duration-500', getColor())}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}
